import { LogisticsCostKey } from "../common/LogisticsCostKey";
import { PurchaseCostKey } from "../common/PurchaseCostKey";
import { BaseModel } from "./BaseModel";

// H1: Component Walk Model
export default class ComponentWalkModel extends BaseModel {
  PartDataSets = [];
  BaselineCurrency = "USD";
  TabName = "";
  TabIndex = 0;
  RowDepth = 0;
  Currency = "USD";
  PtIndex = []; // array of object (ex. {i, slots: [{id: x.Id, ptnum: x.PartNumber, ptname: x.PartName}]} )
  SlotRanges = []; // array of string (ex. 'A2:J20')
  UsedCurrencies = [];
  isExpanded = false;
  SlotColumnLetters = {}; // array of objects that specify the column letters for each slot to update if the slot is expanded

  constructor() {
    super();
  }

  /**
   * @param {number} slotIndex Slot index to insert the model into
   * @param {ComponentWalkPartModel} slotModel Part to insert into the specified slot
   */
  setSlot(slotIndex, slotModel) {
    if (
      slotIndex > 1 ||
      slotModel === undefined ||
      this.PartDataSets[slotIndex]?.PartId === slotModel.PartId
    ) {
      return;
    }

    if (this.PartDataSets[slotIndex] !== undefined) this.resetSlot(slotIndex);

    this.PartDataSets[slotIndex] = slotModel;

    if (this.PartDataSets.length >= 2) {
      this.refreshUsedCurrencies();
      this.renameTab();
      this.processSlots();
    }
  }

  refreshUsedCurrencies() {
    this.UsedCurrencies = new Set([
      this.Currency,
      ...this.PartDataSets[0].AsmBom.filter((x) => x.Material.Currency).map(
        (x) => x.Material.Currency
      ),
      ...this.PartDataSets[1].AsmBom.filter((x) => x.Material.Currency).map(
        (x) => x.Material.Currency
      ),
    ]);
  }

  resetSlot(slotIndex) {
    this.TabName = "Component Walk";
    this.PartDataSets[slotIndex] = undefined;
    this.PtIndex = [];
    this.SlotRanges = [];
    this.UsedCurrencies = [];
  }

  processSlots() {
    // Build PtIndex to Sort between the two parts

    this.PartDataSets[0].AsmBom.forEach((x, i) =>
      this.PtIndex.push({
        i,
        slots: [
          {
            id: x.Id,
            ptnum: x.PartNumber,
            ptname: x.PartName,
            ptstring: x.PartString,
          },
        ],
      })
    );

    this.match(
      (x, y) =>
        y.slots[0]?.ptstring &&
        y.slots[0].ptstring === x.PartString &&
        y.slots[0]?.ptname === x.PartName
    );

    this.match((x, y) => y.slots[0]?.ptname === x.PartName);

    this.match(
      (x, y) => y.slots[0]?.ptstring && y.slots[0].ptstring === x.PartString
    );

    this.match((x, y) => y.slots[0]?.ptnum === x.PartNumber, true);

    this.RowDepth = this.PtIndex.length;
    for (let i = 0; i < 10; i++) {
      this.PtIndex.push({
        i: this.RowDepth + i,
        slots: [undefined, undefined],
      });
    }
    this.RowDepth = this.PtIndex.length;

    this.SlotRanges.push(`[${this.TabName}]!A2:I${this.RowDepth}`);
    this.SlotRanges.push(`[${this.TabName}]!J2:R${this.RowDepth}`);

    this.isWorkerCompleted = true;
  }

  expand(spreadsheet, buildSheet = true) {
    this.isExpanded = true;

    // spreadsheet.clear({type: "Clear All" });
    // spreadsheet.clearConditionalFormat(`${compDelta.start}${rowoffset + 2}:${compDelta.end}${dPlusCurrency + 3}`)

    // set column letters to reflect expanded view
    this.SlotColumnLetters = {
      slot1: {
        start: "A",
        end: "L",
        startOffset: (offset) => {
          if (offset >= 1) offset += 2; // Compensates for added columns
          if (offset > 11) throw "Out of range for part slot 1";
          return this.excelLetter(1 + offset);
        },
      },
      slot2: {
        start: "M",
        end: "X",
        startOffset: (offset) => {
          if (offset >= 1) offset += 2; // Compensates for added columns
          if (offset > 11) throw "Out of range for part slot 2";
          return this.excelLetter(13 + offset);
        },
      },
      compDelta: {
        start: "Y",
        end: "AB",
        startOffset: (offset) => {
          if (offset > 3) throw "Out of range for component deltas";
          return this.excelLetter(25 + offset);
        },
      },
      comments: { start: "AC", end: "AC" },
    };

    if (buildSheet === false) return;

    this.buildSheet(spreadsheet, false);
  }

  collapse(spreadsheet, buildSheet = true) {
    this.isExpanded = false;
    // set column letters to reflect collapsed view
    this.SlotColumnLetters = {
      slot1: {
        start: "A",
        end: "J",
        startOffset: (offset) => {
          if (offset > 9) throw "Out of range for part slot 1";
          return this.excelLetter(1 + offset);
        },
      },
      slot2: {
        start: "K",
        end: "T",
        startOffset: (offset) => {
          if (offset > 9) throw "Out of range for part slot 2";
          return this.excelLetter(11 + offset);
        },
      },
      compDelta: {
        start: "U",
        end: "X",
        startOffset: (offset) => {
          if (offset > 3) throw "Out of range for component deltas";
          return this.excelLetter(21 + offset);
        },
      },
      comments: { start: "Y", end: "Y" },
    };

    if (buildSheet === false) return;

    this.buildSheet(spreadsheet, false);
  }

  // USED
  buildSheet(spreadsheet, withExportHeaders) {
    if (!this.SlotColumnLetters.slot1) return this.collapse(spreadsheet);

    spreadsheet.clear({ type: "Clear All" });

    this.SlotRanges = [
      `[${this.TabName}]!${this.SlotColumnLetters.slot1.start}2:${this.SlotColumnLetters.slot1.end}${this.RowDepth}`,
      `[${this.TabName}]!${this.SlotColumnLetters.slot2.start}2:${this.SlotColumnLetters.slot2.end}${this.RowDepth}`,
    ];

    let rowoffset = 0;
    let expHeaders = [];

    if (withExportHeaders === true) {
      this.isExpanded = true;
      rowoffset = 8;
      expHeaders = this.addExportHeaders();
    }

    const slotHeaders = [
      {
        value: "PART NUMBER",
        style: this.defaultHeader(),
        wrap: true,
      },
      {
        value: "PART NAME",
        style: this.defaultHeader(),
        wrap: true,
      },
      ...(this.isExpanded
        ? [
            {
              value: "Supplier Name",
              style: this.defaultHeader(),
              wrap: true,
            },
            {
              value: "Freight / Duty Location",
              style: this.defaultHeader(),
              wrap: true,
            },
          ]
        : []),
      {
        value: "QTY / SALEABLE",
        style: this.defaultHeader(),
        wrap: true,
      },
      { value: "UOM", style: this.defaultHeader(), wrap: true },
      { value: "Method", style: this.defaultHeader(), wrap: true },
      {
        value: "MAT'L / DUTY CURR",
        style: this.defaultHeader(),
        wrap: true,
      },
      {
        value: "MAT'L / ASM",
        style: this.defaultHeader(),
        wrap: true,
      },
      {
        value: "DUTY / ASM",
        style: this.defaultHeader(),
        wrap: true,
      },
      {
        value: `FREIGHT / ASM\n(${this.Currency})`,
        style: this.defaultHeader(),
        wrap: true,
      },
      {
        value: `OTHER LOGIS / ASM\n(${this.Currency})`,
        style: this.defaultHeader(),
        wrap: true,
      },
    ];

    const sheet = {
      index: this.TabIndex,
      name: this.TabName,
      rows: [
        ...expHeaders,
        {
          height: 60,
          cells: [
            ...slotHeaders,
            ...slotHeaders,
            {
              value: `DELTA MAT'L\n(${this.Currency})`,
              style: this.defaultHeader(),
              wrap: true,
            },
            {
              value: `DELTA DUTY\n(${this.Currency})`,
              style: this.defaultHeader(),
              wrap: true,
            },
            {
              value: `DELTA FREIGHT\n(${this.Currency})`,
              style: this.defaultHeader(),
              wrap: true,
            },
            {
              value: `DELTA OTHER LOGISTICS\n(${this.Currency})`,
              style: this.defaultHeader(),
              wrap: true,
            },
            {
              value: "COMMENTS",
              style: this.defaultHeader(),
              wrap: true,
            },
          ],
        },
        ...this.generateEmptyRows(
          this.RowDepth,
          this.isExpanded ? 28 : 24,
          rowoffset
        ),
        ...this.generateBottomSumRows(rowoffset),
      ],
      columns: [
        { width: 140 },
        { width: 260 },
        ...(this.isExpanded ? [{ width: 100 }, { width: 100 }] : []),
        { width: 65 },
        { width: 40 },
        { width: 40 },
        { width: 40 },
        { width: 68 },
        { width: 75 },
        { width: 75 },
        { width: 75 },
        { width: 140 },
        { width: 260 },
        ...(this.isExpanded ? [{ width: 100 }, { width: 100 }] : []),
        { width: 65 },
        { width: 40 },
        { width: 40 },
        { width: 40 },
        { width: 68 },
        { width: 75 },
        { width: 75 },
        { width: 75 },
        { width: 80 },
        { width: 80 },
        { width: 80 },
        { width: 80 },
        { width: 150 },
      ],
      ranges: [
        ...this.PartDataSets.map((x, i) => ({
          // Parts
          address: `[${this.TabName}]!${this.excelLetter(
            i * (this.isExpanded ? 12 : 10) + 1
          )}${rowoffset + 2}`,
          dataSource: this.fillPartRange(
            x,
            i,
            undefined,
            undefined,
            this.isExpanded
          ),
          showFieldAsHeader: false,
          startCell: `[${this.TabName}]!${this.excelLetter(
            i * (this.isExpanded ? 12 : 10) + 1
          )}${rowoffset + 2}`,
        })),
        this.generateDeltaRange(rowoffset),
      ],
    };

    if (withExportHeaders === true) {
      spreadsheet.sheets[this.TabIndex].name = sheet.name;
      spreadsheet.sheets[this.TabIndex].rows = sheet.rows;
      spreadsheet.sheets[this.TabIndex].columns = sheet.columns;
      spreadsheet.sheets[this.TabIndex].ranges = sheet.ranges;
      spreadsheet.refresh();
      return;
    }

    //if ( clearFirst === true) spreadsheet.delete(0,23,"Column",this.TabName);

    spreadsheet.ej2Instances.sheets[this.TabIndex].name = sheet.name;
    spreadsheet.ej2Instances.sheets[this.TabIndex].rows = sheet.rows;
    spreadsheet.ej2Instances.sheets[this.TabIndex].columns = sheet.columns;
    spreadsheet.ej2Instances.sheets[this.TabIndex].ranges = sheet.ranges;

    spreadsheet.refresh();
  }

  applyFormatting(spreadsheet, withExportHeaders) {
    const defaultHeaderRightAlign = this.defaultHeader();
    let rowoffset = 0;
    const { slot1, slot2, compDelta, comments } = this.SlotColumnLetters;

    if (withExportHeaders === true) rowoffset = 8;

    const d = this.RowDepth + rowoffset;
    const dPlusCurrency = d + (this.UsedCurrencies.size ?? 0) + 5; // Last row of currency data

    spreadsheet.clearConditionalFormat(
      `A2:${this.SlotColumnLetters.comments.end}${dPlusCurrency + 10}`
    );

    defaultHeaderRightAlign.textAlign = "right";

    spreadsheet.numberFormat(
      "@",
      `${slot1.start}2:${slot1.startOffset(1)}${d}`
    );
    spreadsheet.numberFormat(
      "@",
      `${slot1.startOffset(3)}2:${slot1.startOffset(4)}${d}`
    );
    spreadsheet.numberFormat(
      "@",
      `${slot2.start}2:${slot2.startOffset(1)}${d}`
    );
    spreadsheet.numberFormat(
      "@",
      `${slot2.startOffset(3)}2:${slot2.startOffset(4)}${d}`
    );

    spreadsheet.refresh();

    // Merges
    spreadsheet.merge(`${compDelta.start}${d + 2}:${compDelta.end}${d + 2}`);
    spreadsheet.merge(`${slot1.start}${d + 5}:${slot1.end}${d + 5}`);
    spreadsheet.merge(`${slot2.start}${d + 5}:${slot2.end}${d + 5}`);
    spreadsheet.merge(`${slot1.start}${d + 6}:${slot1.startOffset(1)}${d + 6}`);
    spreadsheet.merge(`${slot2.start}${d + 6}:${slot2.startOffset(1)}${d + 6}`);
    spreadsheet.merge(
      `${slot1.startOffset(2)}${d + 6}:${slot1.startOffset(3)}${d + 6}`
    );
    spreadsheet.merge(
      `${slot2.startOffset(2)}${d + 6}:${slot2.startOffset(3)}${d + 6}`
    );
    spreadsheet.merge(`${compDelta.start}${d + 8}:${compDelta.end}${d + 8}`);
    spreadsheet.merge(
      `${slot1.start}${dPlusCurrency + 3}:${slot1.startOffset(4)}${
        dPlusCurrency + 3
      }`
    ); // !
    spreadsheet.merge(
      `${slot2.start}${dPlusCurrency + 3}:${slot2.startOffset(4)}${
        dPlusCurrency + 3
      }`
    ); // !

    // Cell Formatting
    spreadsheet.cellFormat(
      { textAlign: "right" },
      `${slot1.start}${d + 6}:${slot1.startOffset(1)}${dPlusCurrency}`
    );
    spreadsheet.cellFormat(
      { textAlign: "right" },
      `${slot2.start}${d + 6}:${slot2.startOffset(1)}${dPlusCurrency}`
    );
    spreadsheet.cellFormat(
      { textAlign: "center" },
      `${slot1.startOffset(2)}${d + 6}:${slot1.startOffset(2)}${dPlusCurrency}`
    );
    spreadsheet.cellFormat(
      { textAlign: "center" },
      `${slot2.startOffset(2)}${d + 6}:${slot2.startOffset(2)}${dPlusCurrency}`
    );
    spreadsheet.cellFormat(
      defaultHeaderRightAlign,
      `${slot1.start}${dPlusCurrency + 3}:${slot1.startOffset(2)}${
        dPlusCurrency + 3
      }`
    );
    spreadsheet.cellFormat(
      defaultHeaderRightAlign,
      `${slot2.start}${dPlusCurrency + 3}:${slot2.startOffset(2)}${
        dPlusCurrency + 3
      }`
    );
    spreadsheet.cellFormat(
      this.defaultHeader(),
      `${slot1.start}${d + 5}:${slot2.end}${d + 5}`
    );
    spreadsheet.cellFormat(
      this.defaultHeader(),
      `${compDelta.start}${d + 2}:${compDelta.end}${d + 2}`
    );

    if (this.UsedCurrencies.size > 1) {
      spreadsheet.cellFormat(
        { backgroundColor: "#000" },
        `${slot1.startOffset(8)}${d + 7}:${slot1.end}${dPlusCurrency}`
      );
      spreadsheet.cellFormat(
        { backgroundColor: "#000" },
        `${slot2.startOffset(8)}${d + 7}:${slot2.end}${dPlusCurrency}`
      );
    }

    // Borders
    spreadsheet.setBorder(
      { border: "2px solid #000" },
      `${slot1.start}${rowoffset + 1}:${slot1.startOffset(9)}${d}`,
      "Outer"
    );
    spreadsheet.setBorder(
      { border: "2px solid #000" },
      `${slot2.start}${rowoffset + 1}:${slot2.end}${d}`,
      "Outer"
    );
    spreadsheet.setBorder(
      { border: "2px solid #000" },
      `${compDelta.start}${rowoffset + 1}:${compDelta.end}${d}`,
      "Outer"
    );
    spreadsheet.setBorder(
      { border: "1px solid #000" },
      `${compDelta.start}${d + 2}:${compDelta.end}${d + 3}`
    );
    spreadsheet.setBorder(
      { border: "1px solid #000" },
      `${compDelta.start}${dPlusCurrency + 2}:${compDelta.end}${
        dPlusCurrency + 3
      }`
    );
    spreadsheet.setBorder(
      { border: "1px solid #000" },
      `${slot1.start}${rowoffset + 2}:${comments.end}${d}`,
      "Horizontal"
    );
    spreadsheet.setBorder(
      { border: "2px solid #000" },
      `${comments.start}${rowoffset + 1}:${comments.end}${d}`,
      "Outer"
    );
    spreadsheet.setBorder(
      { border: "1px solid #000" },
      `${slot1.start}${d + 5}:${slot2.end}${dPlusCurrency}`
    );
    spreadsheet.setBorder(
      { border: "1px solid #000" },
      `${slot1.start}${dPlusCurrency + 3}:${compDelta.end}${dPlusCurrency + 3}`
    );

    // Number Formatting
    spreadsheet.numberFormat(
      "#,##0.0000",
      `${slot1.startOffset(2)}${rowoffset + 2}:${slot1.startOffset(2)}${d}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${slot1.startOffset(6)}${rowoffset + 2}:${slot1.startOffset(9)}${
        dPlusCurrency + 3
      }`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${slot1.startOffset(2)}${d + 6}:${slot1.startOffset(2)}${dPlusCurrency}`
    );
    spreadsheet.numberFormat(
      "#,##0.0000",
      `${slot2.startOffset(2)}${rowoffset + 2}:${slot2.startOffset(2)}${d}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${slot2.startOffset(6)}${rowoffset + 2}:${slot2.end}${dPlusCurrency + 3}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${slot2.startOffset(2)}${d + 6}:${slot2.startOffset(2)}${dPlusCurrency}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${compDelta.start}${rowoffset + 2}:${compDelta.end}${d}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${compDelta.start}${d + 3}:${compDelta.end}${d + 3}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `S${dPlusCurrency + 3}:${compDelta.end}${dPlusCurrency + 3}`
    );

    spreadsheet.refresh();

    // Conditional Formatting
    if (withExportHeaders !== true) {
      spreadsheet.conditionalFormat({
        type: "LessThan",
        value: "0",
        cFColor: "GreenFT",
        range: `${compDelta.start}${rowoffset + 2}:${compDelta.end}${
          dPlusCurrency + 5
        }`,
      });
      spreadsheet.conditionalFormat({
        type: "GreaterThan",
        value: "0",
        cFColor: "RedFT",
        range: `${compDelta.start}${rowoffset + 2}:${compDelta.end}${
          dPlusCurrency + 5
        }`,
      });
      spreadsheet.refresh();
    }
  }

  applySlotFormatting(spreadsheet, slot) {
    const d = this.RowDepth;
    const dPlusCurrency = d + (this.UsedCurrencies.size ?? 0) + 5;
    const cols =
      slot === 0 ? this.SlotColumnLetters.slot1 : this.SlotColumnLetters.slot2;
    const { compDelta } = this.SlotColumnLetters;

    spreadsheet.setBorder(
      { border: "2px solid #000" },
      `${cols.start}1:${cols.end}${d}`,
      "Outer"
    );
    spreadsheet.setBorder(
      { border: "1px solid #000" },
      `${cols.start}1:${cols.end}${d}`,
      "Horizontal"
    );
    // spreadsheet.cellFormat("@", `B2:B${d}`);
    spreadsheet.numberFormat(
      "#,##0.0000",
      `${cols.startOffset(2)}2:${cols.startOffset(2)}${d}`
    );
    spreadsheet.numberFormat(
      "_(#,##0.0000_);(#,##0.0000);",
      `${cols.startOffset(5)}2:${cols.end}${d}`
    );

    spreadsheet.conditionalFormat({
      type: "LessThan",
      value: "0",
      cFColor: "GreenFT",
      range: `${compDelta.start}2:${compDelta.end}${dPlusCurrency + 5}`,
    });
    spreadsheet.conditionalFormat({
      type: "GreaterThan",
      value: "0",
      cFColor: "RedFT",
      range: `${compDelta.start}2:${compDelta.end}${dPlusCurrency + 5}`,
    });

    spreadsheet.refresh();
  }

  // USED
  addExportHeaders() {
    let boldStyle = { fontWeight: "bold", textAlign: "right" };
    let textStyle = { textAlign: "left" };
    let hrows = [
      {
        cells: [
          {
            colSpan: 11,
            value: "BASE MATERIAL COST DATA",
            style: this.defaultHeader(),
          },
          ...this.createEmptyCells(11),
          {
            colSpan: 11,
            value: "COMPARE-TO MATERIAL COST DATA",
            style: this.defaultHeader(),
          },
          ...this.createEmptyCells(11),
          {
            colSpan: 4,
            value: "DELTA",
            style: this.defaultHeader(),
          },
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "Customer Platform:", style: boldStyle },
          {
            colSpan: 7,
            value: this.PartDataSets[0].CustomerPlatform,
            style: textStyle,
          },
          ...this.createEmptyCells(10, 3),
          { value: "Customer Platform:", style: boldStyle },
          {
            colSpan: 7,
            value: this.PartDataSets[1].CustomerPlatform,
            style: textStyle,
          },
          ...this.createEmptyCells(9),
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "ECR Number:", style: boldStyle },
          {
            colSpan: 3,
            value: this.PartDataSets[0].EcrNumber,
            style: textStyle,
          },
          ...this.createEmptyCells(6),
          { value: "Rev:", style: boldStyle },
          { value: this.PartDataSets[0].Rev, style: textStyle },
          ...this.createEmptyCells(2),
          { value: "ECR Number:", style: boldStyle },
          {
            colSpan: 3,
            value: this.PartDataSets[1].EcrNumber,
            style: textStyle,
          },
          ...this.createEmptyCells(6),
          { value: "Rev:", style: boldStyle },
          { value: this.PartDataSets[1].Rev, style: textStyle },
          ...this.createEmptyCells(4),
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "Option:", style: boldStyle },
          {
            colSpan: 3,
            value: this.PartDataSets[0].Option,
            style: textStyle,
          },
          ...this.createEmptyCells(6),
          { value: "Suffix:", style: boldStyle },
          { value: this.PartDataSets[0].Suffix, style: textStyle },
          ...this.createEmptyCells(2),
          { value: "Option:", style: boldStyle },
          {
            colSpan: 3,
            value: this.PartDataSets[1].Option,
            style: textStyle,
          },
          ...this.createEmptyCells(6),
          { value: "Suffix:", style: boldStyle },
          { value: this.PartDataSets[1].Suffix, style: textStyle },
          ...this.createEmptyCells(4),
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "Saleable Part Number:", style: boldStyle },
          {
            colSpan: 3,
            value: this.PartDataSets[0].PartNumber,
            style: textStyle,
          },
          ...this.createEmptyCells(10),
          { value: "Saleable Part Number:", style: boldStyle },
          {
            colSpan: 3,
            value: this.PartDataSets[1].PartNumber,
            style: textStyle,
          },
          ...this.createEmptyCells(9),
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "Saleable Part Name:", style: boldStyle },
          {
            colSpan: 7,
            value: this.PartDataSets[0].PartName,
            style: textStyle,
          },
          ...this.createEmptyCells(10),
          { value: "Saleable Part Name:", style: boldStyle },
          {
            colSpan: 7,
            value: this.PartDataSets[1].PartName,
            style: textStyle,
          },
          ...this.createEmptyCells(9),
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "Version:", style: boldStyle },
          {
            colSpan: 7,
            value: this.PartDataSets[0].VersionName,
            style: textStyle,
          },
          ...this.createEmptyCells(10),
          { value: "Version:", style: boldStyle },
          {
            colSpan: 7,
            value: this.PartDataSets[1].VersionName,
            style: textStyle,
          },
          ...this.createEmptyCells(9),
        ],
      },
      {
        cells: [
          { value: "" },
          { value: "Max Annual Volume:", style: boldStyle },
          {
            colSpan: 2,
            value: Math.max(
              ...this.PartDataSets[0].Volumes.map((x) => x.volume)
            ),
            style: textStyle,
          },
          ...this.createEmptyCells(5),
          { colSpan: 2, value: "1st Vol Yr:", style: boldStyle },
          { value: "" },
          { value: this.PartDataSets[0].FirstVolYear, style: textStyle },
          ...this.createEmptyCells(2),
          { value: "Max Annual Volume:", style: boldStyle },
          {
            colSpan: 2,
            value: Math.max(
              ...this.PartDataSets[1].Volumes.map((x) => x.volume)
            ),
            style: textStyle,
          },
          ...this.createEmptyCells(5),
          { colSpan: 2, value: "1st Vol Yr:", style: boldStyle },
          { value: "" },
          { value: this.PartDataSets[1].FirstVolYear, style: textStyle },
          ...this.createEmptyCells(2),
        ],
      },
    ];

    return hrows;
  }

  createEmptyCells(count, idxoffset) {
    const output = Array(count);
    for (let i = 0; i < count; i++) {
      if (idxoffset) output[i] = { index: i + idxoffset, value: "" };
      else output[i] = { value: "" };
    }
    return output;
  }

  sumPartCosts(partdata, currency, year) {
    let output = Array(4).fill(0);
    if (year === undefined) {
      year = partdata.DisplayYear ?? partdata.FirstVolYear;
    }

    if (currency === undefined) {
      currency = partdata.BaselineCurrency;
    }

    // Get Rate
    let rate = partdata.getRate(currency, year);

    partdata.AsmBom.forEach((x) => {
      // Costs per Unit
      let cost = partdata.exch(
        x.Material.Cost,
        partdata.getRate(x.Material.Currency),
        rate
      );
      let freight = partdata.exch(
        x.Freight.Cost,
        partdata.getRate(x.Freight.Currency),
        rate
      );
      let pack = partdata.exch(
        x.PackagingCost.Cost,
        partdata.getRate(x.PackagingCost.Currency),
        rate
      );
      let warehouse = partdata.exch(
        x.WarehouseCost.Cost,
        partdata.getRate(x.WarehouseCost.Currency),
        rate
      );
      let expdunn = partdata.exch(
        x.ExpendableDunnage.Cost,
        partdata.getRate(x.ExpendableDunnage.Currency),
        rate
      );

      let duty = !isNaN(x.Duty.Percent)
        ? partdata.calcDutyCost(
            [cost, freight, pack, warehouse, expdunn],
            x.Duty
          )
        : 0;

      output[0] += cost * x.QtyPerSaleable;
      output[1] += freight * x.QtyPerSaleable;
      output[2] += duty * x.QtyPerSaleable;
      output[3] += x.QtyPerSaleable * (pack + warehouse + expdunn);
    });

    return output;
  }

  generateDeltaRange(rowoffset = 0) {
    let rows = [];
    const { compDelta } = this.SlotColumnLetters;

    this.PtIndex.forEach((x, i) => {
      // use line data to calculate deltas
      const deltas = this.calculateDelta(rowoffset, (i + 2));
      if (i === this.RowDepth - 1) return;
      rows.push(deltas);
    });

    return {
      address: `[${this.TabName}]!${compDelta.start}${rowoffset + 2}`,
      dataSource: rows,
      showFieldAsHeader: false,
      startCell: `[${this.TabName}]!${compDelta.start}${rowoffset + 2}`,
    };
  }

  reprocessDeltas(spreadsheet) {
    spreadsheet.updateRange(this.generateDeltaRange());
    spreadsheet.refresh();
  }

  generateDeltaSumRange(rowoffset = 0) {
    let d = this.RowDepth + rowoffset;
    const { compDelta } = this.SlotColumnLetters;

    return {
      // Delta Sum
      address: `[${this.TabName}]!${compDelta.start}${d + 2}`,
      dataSource: [
        [`DELTA ${this.Currency}`],
        [
          `=SUM(${compDelta.start}${rowoffset + 2}:${compDelta.start}${d})`,
          `=SUM(${compDelta.startOffset(1)}${rowoffset + 2}:${compDelta.startOffset(1)}${d})`,
          `=SUM(${compDelta.startOffset(2)}${rowoffset + 2}:${compDelta.startOffset(2)}${d})`,
          `=SUM(${compDelta.end}${rowoffset + 2}:${compDelta.end}${d})`,
        ],
      ],
      showFieldAsHeader: false,
      startCell: `[${this.TabName}]!${compDelta.start}${d + 2}`,
    };
  }

  // generatePartSumsRange(rowoffset = 0) {
  //   let d = this.RowDepth + rowoffset;
  //   const { slot1, slot2 } = this.SlotColumnLetters;

  //   return {
  //     // Individual Part Sums
  //     address: `[${this.TabName}]!${slot1.start}${d + 5}`,
  //     dataSource: [
  //       [
  //         "BASE SUBTOTALS BY CURRENCY",
  //         ...new Array(this.isExpanded ? 10:8).fill(""),
  //         "COMPARE-TO SUBTOTALS BY CURRENCY",
  //         ...new Array(this.isExpanded ? 10:8).fill(""),
  //       ],
  //       [
  //         `USD ${this.PartDataSets[0].DisplayYear} FX`,
  //         "",
  //         `${
  //           this.PartDataSets[0].ExchangeRates.find(
  //             (x) =>
  //               x.currency === this.Currency &&
  //               x.year === this.PartDataSets[0].DisplayYear
  //           )?.value
  //         }`,
  //         "",
  //         this.Currency,
  //         `=SUM(${slot1.startOffset(5)}${rowoffset + 2}:${slot1.startOffset(5)}${d})`,
  //         `=SUM(${slot1.startOffset(6)}${rowoffset + 2}:${slot1.startOffset(6)}${d})`,
  //         `=SUM(${slot1.startOffset(7)}${rowoffset + 2}:${slot1.startOffset(7)}${d})`,
  //         `=SUM(${slot1.end}${rowoffset + 2}:${slot1.end}${d})`,
  //         `USD ${this.PartDataSets[1].DisplayYear} FX`,
  //         "",
  //         `${
  //           this.PartDataSets[1].ExchangeRates.find(
  //             (x) =>
  //               x.currency === this.Currency &&
  //               x.year === this.PartDataSets[1].DisplayYear
  //           )?.value
  //         }`,
  //         "",
  //         this.Currency,
  //         `=SUM(${slot2.startOffset(5)}${rowoffset + 2}:${slot2.startOffset(5)}${d})`,
  //         `=SUM(${slot2.startOffset(6)}${rowoffset + 2}:${slot2.startOffset(6)}${d})`,
  //         `=SUM(${slot2.startOffset(7)}${rowoffset + 2}:${slot2.startOffset(7)}${d})`,
  //         `=SUM(${slot2.end}${rowoffset + 2}:${slot2.end}${d})`,
  //       ],
  //     ],
  //     showFieldAsHeader: false,
  //     startCell: `[${this.TabName}]!${slot1.start}${d + 5}`,
  //   };
  // }

  // USED
  generateBottomSumRows(rowoffset = 0) {
    const output = [];
    let d = this.RowDepth + rowoffset;
    const usdTotalsLine = d + (this.UsedCurrencies.size ?? 0) + 8;
    const { compDelta, slot1, slot2 } = this.SlotColumnLetters;

    // Delta Sums
    output.push(
      {
        index: d + 2,
        cells: [
          ...this.createEmptyCells(this.isExpanded ? 24 : 20),
          {
            colSpan: 4,
            value: `DELTA ${this.Currency}`,
            style: this.defaultHeader(),
          },
        ],
      },
      {
        index: d + 3,
        cells: [
          ...this.createEmptyCells(this.isExpanded ? 24 : 20),
          {
            formula: `=SUM(${compDelta.start}${rowoffset + 2}:${
              compDelta.start
            }${d})`,
          },
          {
            formula: `=SUM(${compDelta.startOffset(1)}${
              rowoffset + 2
            }:${compDelta.startOffset(1)}${d})`,
          },
          {
            formula: `=SUM(${compDelta.startOffset(2)}${
              rowoffset + 2
            }:${compDelta.startOffset(2)}${d})`,
          },
          {
            formula: `=SUM(${compDelta.end}${rowoffset + 2}:${
              compDelta.end
            }${d})`,
          },
        ],
      },
      ...this.generateEmptyRows(1, 26, d + 4)
    );

    // PartSums
    output.push(
      {
        cells: [
          {
            colSpan: this.isExpanded ? 12 : 10,
            value: `BASE SUBTOTALS BY CURRENCY`,
            style: this.defaultHeader(),
          },
          ...this.createEmptyCells(this.isExpanded ? 11 : 9),
          {
            colSpan: this.isExpanded ? 12 : 10,
            value: `COMPARE-TO SUBTOTALS BY CURRENCY`,
            style: this.defaultHeader(),
          },
          ...this.createEmptyCells(this.isExpanded ? 11 : 9),
        ],
      },
      ...this.currencySumRows(rowoffset + 2),
      ...this.generateEmptyRows(1, 26, d + 7)
    );

    // USD Part Sums
    let pt1USDSums = this.sumPartCosts(this.PartDataSets[0], "USD");
    let pt2USDSums = this.sumPartCosts(this.PartDataSets[1], "USD");
    output.push(
      {
        index: d + 8 + this.UsedCurrencies.size,
        cells: [
          ...this.createEmptyCells(this.isExpanded ? 24 : 20),
          {
            colSpan: 4,
            value: `DELTA USD`,
            style: this.defaultHeader(),
          },
        ],
      },
      {
        index: d + 9,
        cells: [
          { colSpan: this.isExpanded ? 8 : 6, value: "BASE TOTAL IN USD" },
          ...this.createEmptyCells(this.isExpanded ? 7 : 5),
          { value: pt1USDSums[0] },
          { value: pt1USDSums[2] },
          { value: pt1USDSums[1] },
          { value: pt1USDSums[3] },
          {
            colSpan: this.isExpanded ? 8 : 6,
            value: "COMPARE-TO TOTAL IN USD",
          },
          ...this.createEmptyCells(this.isExpanded ? 7 : 5),
          { value: pt2USDSums[0] },
          { value: pt2USDSums[2] },
          { value: pt2USDSums[1] },
          { value: pt2USDSums[3] },
          {
            formula: `=IF(AND(${slot1.startOffset(
              6
            )}${usdTotalsLine}="",${slot2.startOffset(
              6
            )}${usdTotalsLine}=""),"",IF(${slot1.startOffset(
              6
            )}${usdTotalsLine}="",0,${slot1.startOffset(
              6
            )}${usdTotalsLine})-IF(${slot2.startOffset(
              6
            )}${usdTotalsLine}="",0,${slot2.startOffset(6)}${usdTotalsLine}))`,
          },
          {
            formula: `=IF(AND(${slot1.startOffset(
              7
            )}${usdTotalsLine}="",${slot2.startOffset(
              7
            )}${usdTotalsLine}=""),"",IF(${slot1.startOffset(
              7
            )}${usdTotalsLine}="",0,${slot1.startOffset(
              7
            )}${usdTotalsLine})-IF(${slot2.startOffset(
              7
            )}${usdTotalsLine}="",0,${slot2.startOffset(7)}${usdTotalsLine}))`,
          },
          {
            formula: `=IF(AND(${slot1.startOffset(
              8
            )}${usdTotalsLine}="",${slot2.startOffset(
              8
            )}${usdTotalsLine}=""),"",IF(${slot1.startOffset(
              8
            )}${usdTotalsLine}="",0,${slot1.startOffset(
              8
            )}${usdTotalsLine})-IF(${slot2.startOffset(
              8
            )}${usdTotalsLine}="",0,${slot2.startOffset(8)}${usdTotalsLine}))`,
          },
          {
            formula: `=IF(AND(${slot1.end}${usdTotalsLine}="",${slot2.end}${usdTotalsLine}=""),"",IF(${slot1.end}${usdTotalsLine}="",0,${slot1.end}${usdTotalsLine})-IF(${slot2.end}${usdTotalsLine}="",0,${slot2.end}${usdTotalsLine}))`,
          },
        ],
      }
    );

    return output;
  }

  /**
   * Generates the given number of empty rows with the specified number of empty columns
   * @param {number} rows
   * @param {number} col
   * @param {number} rowoffset
   * @returns {Array<object>}
   */
  generateEmptyRows(rows = 0, col = 0, rowoffset = 0) {
    var output = [];

    for (let i = 0; i < rows; i++) {
      let row = rowoffset ? { index: rowoffset + i, cells: [] } : { cells: [] };

      for (let j = 0; j < col; j++) {
        row.cells.push({ index: j, value: "" });
      }

      output.push(row);
    }

    return output;
  }

  /**
   * Generates the currency sum rows for all currencies represented in the part
   * @param {number} rowoffset
   * @returns {Array<object>}
   */
  currencySumRows(rowoffset = 0) {
    const output = [];
    const currencies = [...new Set([this.Currency, ...this.UsedCurrencies])];
    const { slot1, slot2 } = this.SlotColumnLetters;

      currencies.forEach((currency, i) => {
      const pt1CurrencySums = this.sumComponentCostDutyCosts(
        this.PartDataSets[0],
        currency
      );
      const pt2CurrencySums = this.sumComponentCostDutyCosts(
        this.PartDataSets[1],
        currency
          );
      output.push({
        index: rowoffset + i,
        cells: [
          {
            colSpan: this.isExpanded ? 4 : 2,
            value: `USD ${this.PartDataSets[0].DisplayYear} FX`,
          },
          ...this.createEmptyCells(this.isExpanded ? 3 : 1),
          {
            colSpan: 3,
            value: `${
              this.PartDataSets[0].ExchangeRates.find(
                (x) =>
                  x.currency === currency &&
                  x.year === this.PartDataSets[0].DisplayYear
              )?.value
            }`,
          },
          { value: "" },
          { value: "" },
          { value: currency },
          { value: pt1CurrencySums.cost },
          { value: pt1CurrencySums.duty },
          {
            formula:
              currency === this.Currency
                ? `=SUM(${slot1.startOffset(8)}${rowoffset}:${slot1.startOffset(
                    8
                  )}${this.RowDepth + rowoffset - 2})`
                : "",
          },
          {
            formula:
              currency === this.Currency
                ? `=SUM(${slot1.end}${rowoffset}:${slot1.end}${
                    this.RowDepth + rowoffset - 2
                  })`
                : "",
          },
          {
            colSpan: this.isExpanded ? 4 : 2,
            value: `USD ${this.PartDataSets[1].DisplayYear} FX`,
          },
          ...this.createEmptyCells(this.isExpanded ? 3 : 1),
          {
            colSpan: 3,
            value: `${
              this.PartDataSets[1].ExchangeRates.find(
                (x) =>
                  x.currency === currency &&
                  x.year === this.PartDataSets[1].DisplayYear
              )?.value
            }`,
          },
          { value: "" },
          { value: "" },
          { value: currency },
          { value: pt2CurrencySums.cost },
          { value: pt2CurrencySums.duty },
          {
            formula:
              currency === this.Currency
                ? `=SUM(${slot2.startOffset(8)}${rowoffset}:${slot2.startOffset(
                    8
                  )}${this.RowDepth + rowoffset - 2})`
                : "",
          },
          {
            formula:
              currency === this.Currency
                ? `=SUM(${slot2.end}${rowoffset}:${slot2.end}${
                    this.RowDepth + rowoffset - 2
                  })`
                : "",
          },
        ],
      });
    });

    return output;
  }

  /**
   * Calculates the cost and duty for the part in the given currency
   * @param {*} partdata
   * @param {*} currency
   * @returns
   */
  sumComponentCostDutyCosts(partdata, currency) {
    const comps = partdata.AsmBom.filter(
      (x) => (x.Material.Currency ?? "USD") === currency
      );
      const cost = comps.reduce(
          (acc, cur) => acc + (cur.Material.Cost ?? 0) * (cur.QtyPerSaleable ?? 0),
          0
      );

      let duty = 0;
      let year = partdata.DisplayYear;
      comps.forEach(cur => {
          const rate = partdata.getRate(currency, year);
          console.log("------------------");
          console.log(currency, rate);
          var _duty = !isNaN(cur.Duty.Percent) ? partdata.calcDutyCost(
              [
                  partdata.exch(cur.Material.Cost, partdata.getRate(cur.Material.Currency), rate),
                  partdata.exch(cur.Freight.Cost, partdata.getRate(cur.Freight.Currency), rate),
                  partdata.exch(cur.PackagingCost.Cost, partdata.getRate(cur.PackagingCost.Currency), rate),
                  partdata.exch(cur.WarehouseCost.Cost, partdata.getRate(cur.WarehouseCost.Currency), rate),
                  partdata.exch(cur.ExpendableDunnage.Cost, partdata.getRate(cur.ExpendableDunnage.Currency), rate),
              ],
              cur.Duty
          ) : 0;


          duty += _duty * cur.QtyPerSaleable
      })
 
      return { cost, duty };
  }

  /**
   * Gets the part slot index of the cell range selected this only applies for a single row
   * @param {string} cellrange (i.e. "A1:A1")
   * @returns {object} if undefined the range provided is either outside of the slot ranges or spans multiple slots or rows
   */
  getPartSlotRowRange(cellRange) {
    if (cellRange === undefined) return undefined;

    let output = undefined;

    const reg =
      /(\[(?<Table>.*)\]!)?(?<Column1>[a-zA-Z]+)(?<Row1>[0-9]+)(:?(?<Column2>[a-zA-Z]+)(?<Row2>[0-9]+))?/g;
    //const reg = /(?<Column>[a-zA-Z]+)(?<Row>[0-9]+)/g;

    const cellRangeCoords = [...cellRange.matchAll(reg)][0].groups;

    this.SlotRanges.forEach((x, i) => {
      //const slotRangeCoords = [...x.matchAll(reg)].map(x => x.groups);
      const slotRangeCoords = [...x.matchAll(reg)][0].groups;

      const inSlotCols =
        cellRangeCoords.Column1 >= slotRangeCoords.Column1 &&
        cellRangeCoords.Column2 <= slotRangeCoords.Column2;
      const inSlotRows =
        +cellRangeCoords.Row1 >= +slotRangeCoords.Row1 &&
        +cellRangeCoords.Row2 <= +slotRangeCoords.Row2;

      if (
        inSlotCols === true &&
        inSlotRows === true &&
        +cellRangeCoords.Row1 === +cellRangeCoords.Row2
      ) {
        output = {
          slot: i,
          row: cellRangeCoords.Row1,
          range: `${slotRangeCoords.Column1}${cellRangeCoords.Row1}:${slotRangeCoords.Column2}${cellRangeCoords.Row1}`,
          ptIndex: cellRangeCoords.Row1 - 2,
        };
      }
    });
    return output;
  }

  /**
   * Initial load of PtIndex to match first by part number
   * @param {MatchFilter} filter
   * @param {boolean} isLast
   */
  match(filter, isLast = false) {
    this.PartDataSets[1].AsmBom.forEach((x) => {
      if (this.PtIndex.find((y) => y.slots[1]?.id === x.Id)) return;

      let idx = this.PtIndex.find((y) => y.slots?.length === 1 && filter(x, y));

      if (idx === undefined && isLast) {
        idx = { i: this.PtIndex.length, slots: [undefined] };
        this.PtIndex.push(idx);
      }

      if (idx === undefined && isLast === false) return;

      idx.slots.push({
        id: x.Id,
        ptnum: x.PartNumber,
        ptname: x.PartName,
        ptstring: x.PartString,
      });
    });
  }

  /**
   * Populates an object in the output for each displayed row whether it is empty or not
   * @param {*} part
   * @param {*} slot
   * @param {*} currency
   * @param {*} year
   * @param {boolean} expanded
   * @returns {Array<object>}
   */
  fillPartRange(part, slot, currency, year, expanded = false) {
    const output = [];

    if (currency !== undefined) this.Currency = currency;
    this.PtIndex.forEach((x) => {
      let pt = part.AsmBom.find((y) => y.Id === x.slots[slot]?.id);

      if (pt === undefined || pt === null) {
        output.push({
          "PART NUMBER": "",
          "PART NAME": "",
          ...(expanded
            ? {
                "SUPPLIER NAME": "",
                "FREIGHT / DUTY LOCATION": "",
              }
            : {}),
          "QTY / SALEABLE": "",
          UOM: "",
          Method: "",
          "MAT'L / DUTY CURR": "",
          "MAT'L / ASM": "",
          "DUTY / ASM": "",
          "FREIGHT / ASM": "",
          "OTHER LOGIS / ASM": "",
        });
      } else {
        output.push(
          part.calcSinglePartSheetFormat(pt, this.Currency, year, expanded)
        );
      }
    });
    return output;
  }

  /**
   * Calculates the individual component deltas in the users selected currency
   */
  calculateDelta(rowOffset, rowNum) {
    const { slot1, slot2 } = this.SlotColumnLetters;

    const subtotalsStartRow = this.RowDepth + rowOffset + 6;
    const subtotalsEndRow = this.RowDepth + rowOffset + 6 + this.UsedCurrencies.size;

    const fxLoookupFormulaS1 = `INDEX(${slot1.startOffset(2)}${subtotalsStartRow}:${slot1.startOffset(2)}${subtotalsEndRow}, MATCH(${slot1.startOffset(5)}${rowOffset + rowNum}, ${slot1.startOffset(5)}${subtotalsStartRow}:${slot1.startOffset(5)}${subtotalsEndRow}, 0))`
    const fxLoookupFormulaS2 = `INDEX(${slot2.startOffset(2)}${subtotalsStartRow}:${slot2.startOffset(2)}${subtotalsEndRow}, MATCH(${slot2.startOffset(5)}${rowOffset + rowNum}, ${slot2.startOffset(5)}${subtotalsStartRow}:${slot2.startOffset(5)}${subtotalsEndRow}, 0))`

      //https://localhost:44324/walk/component?part=3046804001%7C308ba518-10cf-4030-857b-f6cd74c0ab32%7CA%7C38303761%7CC%7C005&part=3044417001%7C3e29c046-3f61-4e2b-90ac-9f56a2bc9be9%7CA%7C38303761%7C%7C004
      const matlDeltaFormula = `=IFERROR(IF((${slot1.startOffset(6)}${rowOffset + rowNum}) = "", 0, (${slot1.startOffset(6)}${rowOffset + rowNum}/${fxLoookupFormulaS1}))-(IF((${slot2.startOffset(6)}${rowOffset + rowNum})  = "", 0, ${slot2.startOffset(6)}${rowOffset + rowNum}/${fxLoookupFormulaS2})), "")`
      const dutyDeltaFormula = `=IFERROR(IF((${slot1.startOffset(7)}${rowOffset + rowNum})  = "", 0, (${slot1.startOffset(7)}${rowOffset + rowNum}/${fxLoookupFormulaS1}))-(IF((${slot2.startOffset(7)}${rowOffset + rowNum})  = "", 0, ${slot2.startOffset(7)}${rowOffset + rowNum}/${fxLoookupFormulaS2})), "")`
      const freightDeltaFormula = `=IFERROR(IF((${slot1.startOffset(8)}${rowOffset + rowNum})  = "", 0, (${slot1.startOffset(8)}${rowOffset + rowNum}))-(IF((${slot2.startOffset(8)}${rowOffset + rowNum})  = "", 0, ${slot2.startOffset(8)}${rowOffset + rowNum})), "")`
      const otherDeltaFormula = `=IFERROR(IF((${slot1.startOffset(9)}${rowOffset + rowNum})  = "", 0, (${slot1.startOffset(9)}${rowOffset + rowNum}))-(IF((${slot2.startOffset(9)}${rowOffset + rowNum})  = "", 0, ${slot2.startOffset(9)}${rowOffset + rowNum})), "")`

    const output = [matlDeltaFormula, dutyDeltaFormula, freightDeltaFormula, otherDeltaFormula]
    return output;
  }

  /**
   * Generates the new name for the spreadsheet tab
   */
  renameTab() {
    if (this.PartDataSets.length == 2) {
      var name = `${this.PartDataSets[0].getDisplayName()} | ${this.PartDataSets[1].getDisplayName()} - Walk`;

      this.TabName = name;
    }
  }
}

// Component Walk Part Model
export class ComponentWalkPartModel {
  EcrNumber = "";
  Version = "";
  VersionName = "";
  PartNumber = "";
  PartName = "";
  Rev = "";
  Suffix = "";
  Option = "";
  CustomerPlatform = "";
  Volumes = [];
  FirstVolYear = 0;
  DisplayYear = 0;
  ExchangeRates = [];
  AsmBom = [];
  BaselineCurrency = "USD";
  PartId = "";

  constructor(
    partData,
    { GroupName, SaleableParts, Purchases, Logistics },
    exchangeRates
  ) {
    this.Option = GroupName;
    this.ExchangeRates = exchangeRates;

    SaleableParts.forEach(
      ({ Name, Part, Bom, CustomerPlatform, CostStudyVolume }) => {
        if (
          Part.Number.Fields[0] != partData.number ||
          Part.Suffix?.Fields[0].Fields[0] != partData.suffix ||
          Part.DrawingRevision?.Fields[0].Fields[0] != partData.rev
        )
          return;

        // Fill in Saleable Part Details
        this.PartNumber = Part.Number?.Fields[0];
        this.PartName = Name?.Value?.Fields[0];
        this.Rev = Part.DrawingRevision?.Fields[0].Fields[0];
        this.Suffix = Part.Suffix?.Fields[0].Fields[0];
        this.CustomerPlatform = CustomerPlatform?.Value?.Fields[0];

        const volumes = CostStudyVolume?.Value?.Fields[0].Values;

        // First Volume Year
        const volYears = volumes.map((x) => new Date(x.Date).getUTCFullYear());
        this.FirstVolYear = Math.min(...volYears);
        this.DisplayYear = this.FirstVolYear;

        // Max Volume for Years Included in the Study
        this.Volumes = volumes.map((x) => {
          return { year: new Date(x.Date).getUTCFullYear(), volume: x.Value };
        });

        const ptarray = Bom.value.parts.concat(...Bom.value.partGroups);

        ptarray.forEach((part) =>
          this.processBomPart(part, Logistics, Purchases)
        );
      }
    );
  }

  /**
   * Builds the display name for the component
   * @returns {string}
   */
  getDisplayName() {
    let suffix = this.Suffix ? `-${this.Suffix}` : "";
    let rev = this.Rev ? ` ${this.Rev}` : "";

    return `${this.PartNumber}${suffix}${rev}`;
  }

  /**
   * Extracts the relevant data from the part and adds it to the AsmBom array
   * @param {object} part
   * @param {object} logistics
   * @param {object} purchases
   */
  processBomPart(part, logistics, purchases) {
    // let ptNum = part.partId.partNumber;
    // let rev = part.partId.drawingRevision ?? null;
    // let suffix = part.partId.suffix ?? null;
    let key = LogisticsCostKey.buildKeyFromBomComponent(part);

    let partLog = logistics.find((x) =>
      LogisticsCostKey.compare(key, LogisticsCostKey.buildKeyFromLogistics(x))
    );
    let partCost = purchases.find((x) =>
      PurchaseCostKey.compare(key, PurchaseCostKey.buildKeyFromPurchase(x))
    );

    // let partLog = logistics.find(
    //   (x) =>
    //     x.Part.Number?.Fields[0] === ptNum &&
    //     x.Part.Suffix?.Fields[0]?.Fields[0] === suffix &&
    //     (x.Part.DrawingRevision === null ||
    //       x.Part.DrawingRevision?.Fields[0]?.Fields[0] === rev)
    // );

    // partLog ??= logistics.find(
    //   (x) =>
    //     x.Part.Number?.Fields[0] === ptNum &&
    //     (x.Part.DrawingRevision === null ||
    //       x.Part.DrawingRevision?.Fields[0]?.Fields[0] === rev) &&
    //     (x.Part.Suffix === null ||
    //       x.Part.Suffix?.Fields[0]?.Fields[0] === suffix)
    // );

    // let partCost = purchases.find(
    //   (x) =>
    //     x.Part.Number?.Fields[0] === ptNum &&
    //     x.Part.Suffix?.Fields[0]?.Fields[0] === suffix &&
    //     (x.Part.DrawingRevision === null ||
    //       x.Part.DrawingRevision?.Fields[0]?.Fields[0] === rev)
    // );

    // partCost ??= purchases.find(
    //   (x) =>
    //     x.Part.Number?.Fields[0] === ptNum &&
    //     (x.Part.DrawingRevision === null ||
    //       x.Part.DrawingRevision?.Fields[0]?.Fields[0] === rev) &&
    //     (x.Part.Suffix === null ||
    //       x.Part.Suffix?.Fields[0]?.Fields[0] === suffix)
    // );

    let ptReduced = {
      Id: part.id,
      Level: part.bomLevel.value,
      PartNumber: part.partId.partNumber,
      Rev: part.partId.drawingRevision,
      Suffix: part.partId.suffix,
      PartName: part.name.value,
      PartString: partCost?.PartString,
      QtyPerSaleable: part.quantityPerSaleable,
      UoM: part.unitOfMeasure.value,
      Method: part.productionMethod?.value?.method
      .replace("PurchaseWithParent", "PP")
      .replace("Purchase", "P")
      .replace("Make", "M")
      .replace("Interplant", "IP"),
      SupplierName: partCost?.SupplierName?.Value?.Fields[0],
      LogisticsLocation:
      partCost?.LogisticsLocation?.Value?.Fields[0]?.Fields[0],
      Material: {
      Date: partCost?.MaterialCost.Value?.Fields[0]?.Date,
      Currency: partCost?.MaterialCost.Value?.Fields[0]?.Tag,
      Cost: partCost?.MaterialCost.Value?.Fields[0]?.Values[0]?.Value ?? "",
      },
      Freight: {
      Date: partLog?.FreightCost.Value?.Fields[0]?.Date,
      Currency: partLog?.FreightCost.Value?.Fields[0]?.Tag,
      Cost: partLog?.FreightCost.Value?.Fields[0]?.Values[0]?.Value ?? "",
      },
      Duty: {
      Date: partLog?.DutyPercent.Value?.Fields[0].Values[0]?.Date,
      Percent: partLog?.DutyPercent.Value?.Fields[0].Values[0]?.Value ?? "",
      DrawbackPercent:
        partLog?.DutyDrawbackPercent.Value?.Fields[0]?.Values[0]?.Value,
      },
      PackagingCost: {
      Date: partLog?.PackagingCost.Value?.Fields[0]?.Date,
      Currency: partLog?.PackagingCost.Value?.Fields[0]?.Tag,
      Cost: partLog?.PackagingCost.Value?.Fields[0]?.Values[0]?.Value ?? "",
      },
      ExpendableDunnage: {
      Date: partLog?.ExpendableDunnage.Value?.Fields[0]?.Date,
      Currency: partLog?.ExpendableDunnage.Value?.Fields[0]?.Tag,
      Cost: partLog?.ExpendableDunnage.Value?.Fields[0]?.Values[0]?.Value ?? "",
      },
      WarehouseCost: {
      Date: partLog?.WarehouseCost.Value?.Fields[0]?.Date,
      Currency: partLog?.WarehouseCost.Value?.Fields[0]?.Tag,
      Cost: partLog?.WarehouseCost.Value?.Fields[0]?.Values[0]?.Value ?? "",
      },
    };

    this.AsmBom.push(ptReduced);
  }

  /**
   * Retrieves the exchange rate for the given currency and year
   * @param {string} currency
   * @param {number?} year will default to the FirstVolYear by default if not provided
   * @return {object} rate object from ExchangeRates
   */
    getRate(currency, year) {
    if (!currency) {
        currency = "USD";
    }

    if (year !== undefined && year !== null && year !== 0)
      this.DisplayYear = year;

    return this.ExchangeRates.find(
      (r) => r.currency === currency && r.year === this.DisplayYear
    );
  }

  getComponentUnitCalcs(part, currency, year) {
    if (part === undefined)
      return {
        cost: 0,
        freight: 0,
        pack: 0,
        warehouse: 0,
        expdunn: 0,
        duty: 0,
      };
    if (year === undefined) year = this.DisplayYear;
    if (currency === undefined) currency = this.Currency;

    const rate = this.getRate(currency, year);

    // Costs per Unit
    const cost = part.Material.Cost
      ? this.exch(
          part.Material.Cost,
          this.getRate(part.Material.Currency),
          rate
        )
      : 0;
    const freight = part.Freight.Cost
      ? this.exch(part.Freight.Cost, this.getRate(part.Freight.Currency), rate)
      : 0;
    const pack = part.PackagingCost.Cost
      ? this.exch(
          part.PackagingCost.Cost,
          this.getRate(part.PackagingCost.Currency),
          rate
        )
      : 0;
    const warehouse = part.WarehouseCost.Cost
      ? this.exch(
          part.WarehouseCost.Cost,
          this.getRate(part.WarehouseCost.Currency),
          rate
        )
      : 0;
    const expdunn = part.ExpendableDunnage.Cost
      ? this.exch(
          part.ExpendableDunnage.Cost,
          this.getRate(part.ExpendableDunnage.Currency),
          rate
        )
      : 0;

    // calculated in the context currency then exchanged to the currency matching the original material cost
    const duty = isNaN(part.Duty.Percent)
      ? 0
      : this.calcDutyCost([cost, freight, pack, warehouse, expdunn], part.Duty);

    return { cost, freight, pack, warehouse, expdunn, duty };
  }

  /**
   * Pulls the relevant data adjusts for exchange rates and preps for display
   * @returns Data formatted for the spreadsheet
   */
  calcSinglePartSheetFormat(part, currency, year, expanded = false) {
    const rate = this.getRate(currency, year);
    const partDisplay = `${part.PartNumber}${
      part.Suffix ? `-${part.Suffix}` : ""
    }${part.Rev ? ` Rev.${part.Rev}` : ""}`;
    const { freight, pack, warehouse, expdunn, duty } =
      this.getComponentUnitCalcs(part, currency, year);

    // calculated in the context currency then exchanged to the currency matching the original material cost
    const exchDuty = isNaN(part.Duty.Percent)
      ? 0
      : this.exch(duty, rate, this.getRate(part.Material.Currency));

    const output = {
      "PART NUMBER": partDisplay,
      "PART NAME": part.PartName,
      ...(expanded
        ? {
            "SUPPLIER NAME": part.SupplierName,
            "FREIGHT / DUTY LOCATION": part.LogisticsLocation,
          }
        : {}),
      "QTY / SALEABLE": part.QtyPerSaleable,
      UOM: part.UoM,
      Method: part.Method,
      "MAT'L / DUTY CURR": part.Material.Currency ?? "USD",
      "MAT'L / ASM": part.Material.Cost * part.QtyPerSaleable, // Displayed in original currency
      "DUTY / ASM": exchDuty * part.QtyPerSaleable,
      "FREIGHT / ASM": freight * part.QtyPerSaleable,
      "OTHER LOGIS / ASM": part.QtyPerSaleable * (pack + warehouse + expdunn),
    };
    // if ( output["FREIGHT / ASM"] > 0) console.log(output)
    return output;
  }

  /**
   * Calculate Duty Cost
   * @param {Array[number]} costs - freight, pack, warehouse, expdunn, cost
   * @param {object} duty - Date, Percent, DrawbackPercent
   * @return {number}
   */
    calcDutyCost(costs, { Percent, DrawbackPercent }) {
        let output = 0;

        let sumcost = costs.filter(x => !isNaN(parseFloat(x))).reduce((p, v) => (p += parseFloat(v)), 0.0);

    output =
      sumcost * Percent * (1 - (isNaN(DrawbackPercent) ? 0 : DrawbackPercent));

    return output;
  }

  /**
   * Will exchange a cost value from one currency to another
   * @param {number} cost
   * @param {object} startCurrency
   * @param {object} endCurrency
   */
  exch(cost, startCurrency, endCurrency) {
    if (
      isNaN(cost) === false &&
      isNaN(endCurrency?.value) === false &&
      isNaN(startCurrency?.value) === false
    ) {
      return (cost / startCurrency.value) * endCurrency.value;
    }
    return 0;
  }
}
